import { createLogger } from '@/logger';
import { BaseLiveClient } from '@/transport/live/BaseLiveClient';
import type { LiveClientConfig } from '@/transport/live/LiveClient';
import type { RecorderInboundMessage } from '@/transport/live/messages';

const log = createLogger('WsLiveClient');

type SocketState = 'connecting' | 'open' | 'closed';

/**
 * `ws` implementation of `LiveClient`. Streams event batches over a persistent
 * WebSocket connection. `send()` does not resolve until the server acknowledges
 * the batch (`{type:'ack'}`). If the socket drops, times out, or is disposed
 * before the ack arrives, `send()` rejects so the caller (EventQueue)
 * re-queues the batch instead of silently dropping it.
 *
 * Reconnection uses exponential back-off and gives up after
 * `maxReconnectAttempts`, at which point the transport stays `closed` and
 * the flush loop naturally routes everything through REST.
 */
export class WsLiveClient extends BaseLiveClient {
  readonly name = 'WebSocket';

  private socket: WebSocket | null = null;
  private socketState: SocketState = 'closed';
  private reconnectAttempts = 0;
  // True once a socket has reached the OPEN state at least once. Guards
  // `maybeReconnect()` from firing after a never-opened socket's close:
  // when the initial `connect()` rejects, the caller falls back to REST and
  // a background reconnect would leak an orphaned socket with no listener.
  private wasEverOpen = false;

  constructor(config: LiveClientConfig) {
    super(config);
  }

  isReady(): boolean {
    // A batch already in flight means we can't accept another (the ack channel
    // is single-slot), so report not-ready and let the caller route to REST.
    return this.socket?.readyState === WebSocket.OPEN && this.pendingAck === null;
  }

  sendRaw(msg: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify(msg));
    } catch (err) {
      log.warn('sendRaw failed', err as Error);
    }
  }

  send(events: unknown[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.socket?.readyState !== WebSocket.OPEN) {
        reject(new Error('[WsLiveClient] Socket is not open'));
        return;
      }
      if (this.pendingAck) {
        reject(new Error('[WsLiveClient] A batch is already awaiting acknowledgement'));
        return;
      }

      try {
        this.socket.send(JSON.stringify({ events }));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }

      const timer = setTimeout(() => {
        this.rejectPendingAck(new Error('[WsLiveClient] Timed out waiting for server ack'));
        try {
          this.socket?.close();
        } catch {
          /* ignore */
        }
      }, this.ackTimeoutMs);

      this.pendingAck = { resolve, reject, timer };
    });
  }

  dispose(): void {
    this.disposed = true;
    this.rejectPendingAck(new Error('[WsLiveClient] Transport disposed'));
    this.socket?.close();
    this.socket = null;
    this.socketState = 'closed';
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.disposed) {
        reject(new Error('[WsLiveClient] Transport has been disposed'));
        return;
      }

      this.socketState = 'connecting';
      const socket = new WebSocket(this.buildUrl());
      let settled = false;

      const settle = (fn: () => void): void => {
        if (settled) return;
        settled = true;
        clearTimeout(openTimeout);
        fn();
      };

      const openTimeout = setTimeout(() => {
        settle(() => {
          socket.close();
          reject(new Error('[WsLiveClient] Connection timed out'));
        });
      }, this.openTimeoutMs);

      socket.onopen = () => {
        settle(() => {
          this.socket = socket;
          this.socketState = 'open';
          this.reconnectAttempts = 0;
          this.wasEverOpen = true;
          socket.send(JSON.stringify({ type: 'auth', apiKey: this.apiKey }));
          log.debug('WebSocket connected');
          resolve();
        });
      };

      socket.onmessage = (ev) => {
        try {
          const msg = JSON.parse(
            typeof ev.data === 'string' ? ev.data : ''
          ) as Partial<RecorderInboundMessage>;
          if (!msg || typeof msg.type !== 'string') return;
          if (msg.type === 'ack') {
            this.resolvePendingAck();
          } else if (msg.type === 'control:cursor') {
            this.cursorCallback?.(msg.x!, msg.y!);
          } else if (msg.type === 'control:click') {
            this.clickCallback?.(msg.x!, msg.y!);
          } else if (msg.type === 'control:focus') {
            this.focusCallback?.(msg.x!, msg.y!);
          } else {
            // Cobrowse consent + presence messages and any future typed
            // server-pushed signals. Forward as-is to the registered sink.
            this.messageCallback?.(msg as { type: string; [k: string]: unknown });
          }
        } catch {
          /* ignore non-JSON / unexpected frames */
        }
      };

      socket.onerror = () => {
        settle(() => reject(new Error('[WsLiveClient] Error during open')));
      };

      socket.onclose = (ev) => {
        settle(() => reject(new Error(`[WsLiveClient] Closed before open: ${ev.code}`)));

        if (this.socket === socket) {
          this.socket = null;
          this.socketState = 'closed';
        }
        this.rejectPendingAck(new Error(`[WsLiveClient] Socket closed: ${ev.code}`));

        // Only reconnect if the socket actually opened at some point.
        // Otherwise the caller has already fallen back to REST and a
        // background reconnect would leak an orphaned socket.
        if (this.wasEverOpen) this.maybeReconnect();
      };
    });
  }

  private maybeReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      log.warn('WebSocket reconnect attempts exhausted, using REST fallback');
      return;
    }

    const attempt = ++this.reconnectAttempts;
    const delay = this.baseReconnectDelayMs * 2 ** attempt;
    log.debug(`WebSocket reconnecting in ${delay}ms (attempt ${attempt}, exponential back-off)`);

    setTimeout(() => {
      if (!this.disposed) {
        this.connect().catch(() => {
          // onclose will fire and trigger the next maybeReconnect if needed.
        });
      }
    }, delay);
  }

  private buildUrl(): string {
    const url = new URL('/live', this.url.replace(/^http/, 'ws'));
    url.searchParams.set('session', this.sessionId);
    url.searchParams.set('role', 'recorder');
    return url.toString();
  }
}
